import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscribe to SSE events from the backend.
 * - Reconnects automatically with backoff.
 * - Re-fetches on tab visibility change (browsers throttle setInterval in background).
 * - Exposes connection state via the `useConnectionStatus` hook below.
 *
 * @param {(msg: {type: string, payload: any}) => void} onEvent  callback for each event
 * @param {() => void} onRefresh  called on connect/reconnect/visibility-resume to force a fresh fetch
 * @returns {{connected: boolean}}
 */
export function useRealtimeEvents(onEvent, onRefresh) {
  const esRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const onRefreshRef = useRef(onRefresh);
  const [connected, setConnected] = useState(false);

  // Always keep latest callbacks
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    let closed = false;
    let reconnectTimer = null;

    const setStatus = (v) => {
      // Publica también al store global para que el Layout muestre el banner.
      try { window.__mb_sse_connected = v; window.dispatchEvent(new CustomEvent("mb-sse-status", { detail: v })); } catch { /* noop */ }
      setConnected(v);
    };

    const connect = () => {
      if (closed) return;
      const token = localStorage.getItem("mb_token");
      if (!token) return;
      try {
        const url = `${BACKEND_URL}/api/events?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;

        es.addEventListener("connected", () => {
          setStatus(true);
          // Force a refresh on connect to catch anything missed
          onRefreshRef.current?.();
        });

        es.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            onEventRef.current?.(msg);
          } catch { /* noop */ }
        };

        es.onerror = () => {
          setStatus(false);
          try { es.close(); } catch { /* noop */ }
          esRef.current = null;
          if (!closed) {
            // reintentar en 2s
            reconnectTimer = setTimeout(connect, 2000);
          }
        };
      } catch {
        setStatus(false);
        reconnectTimer = setTimeout(connect, 2000);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Browser may have throttled the connection — refresh immediately
        onRefreshRef.current?.();
        if (!esRef.current) connect();
      }
    };

    connect();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { esRef.current?.close(); } catch { /* noop */ }
      esRef.current = null;
      setStatus(false);
    };
  }, []);

  return { connected };
}

/**
 * Hook auxiliar para componentes que no se suscriben directamente a SSE
 * (p. ej. el Layout global) pero quieren mostrar el estado de conexión.
 *
 * Combina:
 *  - `navigator.onLine` (red del SO).
 *  - El estado del EventSource SSE (publicado por `useRealtimeEvents`).
 */
export function useConnectionStatus() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  // Optimista: asumimos SSE conectado hasta que `useRealtimeEvents` indique lo contrario.
  // Si arrancamos en `false` se vería un parpadeo del banner al cargar la página antes
  // de que el SSE se conecte por primera vez (que suele tomar 200–800 ms).
  const [sse, setSse] = useState(true);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onSse = (e) => setSse(!!e.detail);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("mb-sse-status", onSse);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("mb-sse-status", onSse);
    };
  }, []);

  return { online, sse, connected: online && sse };
}
